import type { SceneDocument, SceneNpcPlacement, SceneStatus } from '../../types/scene';

export const SCENES_COLLECTION = 'scenes';
export const SCENE_USER_PLACEMENTS_COLLECTION = 'scene_user_placements';
export const DEFAULT_SCENE_LIMIT = 60;

export interface SceneMetadataInput {
  name: string;
  description: string;
  tags: string[];
  status: SceneStatus;
}

export interface SceneCompositionInput extends SceneMetadataInput {
  levelDesignId: string;
  backgroundUrl: string | null;
  foregroundUrl: string | null;
  traversalMaskUrl: string | null;
  thumbnailUrl: string | null;
  animatedGifs?: SceneDocument['animatedGifs'];
  worldLayout: SceneDocument['worldLayout'];
  defaultCameraZoom: number;
  playerSpawnTile: { x: number; y: number };
  defaultZone: string;
  zones: SceneDocument['zones'];
  signs: SceneDocument['signs'];
  doors: SceneDocument['doors'];
  annotations?: SceneDocument['annotations'];
  npcPlacements: SceneNpcPlacement[];
  characterScale: SceneDocument['characterScale'];
  characterScaleMultiplier: SceneDocument['characterScaleMultiplier'];
  ambientZoneProfile: SceneDocument['ambientZoneProfile'];
}

export interface SceneNpcPlacementUpsertInput {
  placementId?: string;
  source: SceneNpcPlacement['source'];
  sourceNpcId?: string;
  inlineConfig?: SceneNpcPlacement['inlineConfig'];
  spawnX: number;
  spawnY: number;
  zone: string;
  movementConfig?: SceneNpcPlacement['movementConfig'];
  waypointPath?: SceneNpcPlacement['waypointPath'];
}

export interface SceneSaveResult {
  scene: SceneDocument;
  requestedStatus: SceneStatus;
  persistedStatus: SceneStatus;
  warning: string | null;
}

export const normalizeSceneSaveResult = (
  result: SceneSaveResult | SceneDocument,
  requestedStatus: SceneStatus,
): SceneSaveResult =>
  'scene' in result
    ? result
    : {
        scene: result,
        requestedStatus,
        persistedStatus: result.status,
        warning: null,
      };

export const buildSceneSaveResult = (
  scene: SceneDocument,
  requestedStatus: SceneStatus,
  warning: string | null = null,
): SceneSaveResult => ({
  scene,
  requestedStatus,
  persistedStatus: scene.status,
  warning,
});

export const isPublishStatus = (status: SceneStatus): boolean =>
  status === 'published' || status === 'featured';

export const buildSceneUserPlacementsDocId = (userId: string, sceneId: string): string =>
  `${userId}__${sceneId}`;

export const isSeedLinkedSystemScene = (
  scene: Pick<SceneDocument, 'levelDesignId' | 'ownerId'>,
): boolean => scene.ownerId === 'system' && scene.levelDesignId.trim().startsWith('seed-');
